import ListItemWrapper from '../../ListItemWrapper';
import Column from '../../Column';
import { Link } from 'react-router-dom';
import { buildReportShow } from '../../../../../navigation/appRoutes';
import { useContext, useMemo } from 'react';
import { PortalsContext } from '../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import Typography from '../../../../../kingpin/atoms/Typography';
import { FieldUsageEntities } from './types';
import FieldUsageEntity from './FieldUsageEntity';
import styled from 'styled-components';
import cardTypeCheckers from '../../../../../types/cardTypeCheckers';

const List = styled.ul`
  margin: 0;
  padding-left: 15px;
`;

const ReportFieldUsage = ({
  report,
  usage,
}: {
  report: ReportType;
  usage: FieldUsageEntities;
}) => {
  const { selectedPortal } = useContext(PortalsContext);
  const reportLink = buildReportShow({
    portal: selectedPortal,
    id: report.id,
  });

  const cards = report.canvas?.cards;

  const { chartDefIdsOnReport } = useMemo(() => {
    const chartDefIds = new Set<string>();
    cards.forEach((c) => {
      const content = c?.content;
      if (!content) return;
      if (cardTypeCheckers.isChartDefinition(c)) {
        chartDefIds.add(c.content.chartDefinitionId);
      }
    });
    return { chartDefIdsOnReport: chartDefIds };
  }, [cards]);

  const relevant = useMemo(() => {
    const reportFilters = usage.reportFilters.filter((r) => {
      return r.id === report.id;
    });

    const gadgetDimensions = usage.gadgetDimensions.filter((d) =>
      chartDefIdsOnReport.has(d.id),
    );

    const simpleGrids = usage.simpleGrids.filter((d) =>
      chartDefIdsOnReport.has(d.id),
    );

    const simpleGridSorts = usage.simpleGridSorts.filter((d) =>
      chartDefIdsOnReport.has(d.id),
    );

    return {
      reportFilters,
      gadgetDimensions,
      simpleGrids,
      simpleGridSorts,
    };
  }, [usage, report.id, chartDefIdsOnReport]);

  const hasAny =
    relevant.reportFilters.length > 0 ||
    relevant.gadgetDimensions.length > 0 ||
    relevant.simpleGrids.length > 0 ||
    relevant.simpleGridSorts.length > 0;

  return (
    <ListItemWrapper style={{ height: 'auto', minHeight: 56, padding: 12 }}>
      <div>
        <Column style={{ minHeight: 0 }}>
          <Typography.Body type={'Body 14 Bold'}>
            <Link target={'_blank'} to={reportLink}>
              {report.name}
            </Link>
          </Typography.Body>
        </Column>
        {hasAny && (
          <div style={{ marginTop: 8 }}>
            {relevant.reportFilters.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Typography.Body type={'Body 13 Bold'}>
                  Report Filters ({relevant.reportFilters.length})
                </Typography.Body>
                <List>
                  {relevant.reportFilters.map((rf) => (
                    <FieldUsageEntity name={rf.name} />
                  ))}
                </List>
              </div>
            )}
            {relevant.gadgetDimensions.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Typography.Body type={'Body 13 Bold'}>
                  Gadget Dimensions ({relevant.gadgetDimensions.length})
                </Typography.Body>
                <List>
                  {relevant.gadgetDimensions.map((d) => (
                    <FieldUsageEntity link={''} name={d.name} key={d.id} />
                  ))}
                </List>
              </div>
            )}
            {relevant.simpleGrids.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Typography.Body type={'Body 13 Bold'}>
                  Simple Grids ({relevant.simpleGrids.length})
                </Typography.Body>
                <List>
                  {relevant.simpleGrids.map((d) => (
                    <FieldUsageEntity link={''} name={d.name} key={d.id} />
                  ))}
                </List>
              </div>
            )}
            {relevant.simpleGridSorts.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Typography.Body type={'Body 13 Bold'}>
                  Simple Grid Sorts ({relevant.simpleGridSorts.length})
                </Typography.Body>
                <List>
                  {relevant.simpleGridSorts.map((d) => (
                    <FieldUsageEntity name={d.name} link={''} key={d.id} />
                  ))}
                </List>
              </div>
            )}
          </div>
        )}
      </div>
    </ListItemWrapper>
  );
};

export default ReportFieldUsage;
